/* eslint-disable react-hooks/exhaustive-deps */
import { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Stack, TextField, Divider, Typography, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import fetchDeviceList from '../../utils/fetchDeviceList';
import fetchStreamList from '../../utils/fetchStreamList';
import { appSliceActions } from '../../appSlices/appSlice';
import EnhancedTable from '../table/urlTable';
import axiosInstance from '../../utils/axiosInstance';

const headCells = [
  {
    id: 'serial_number',
    numeric: false,
    disablePadding: true,
    label: 'S.No.',
  },
  {
    id: 'property',
    numeric: false,
    disablePadding: true,
    label: 'Name',
  },
  {
    id: 'value',
    numeric: false,
    disablePadding: false,
    label: 'Camera status',
  },
];

export default function CameraInfo(props) {
  const { cameraInfo, camera } = props;
  console.log('props: ', cameraInfo, camera);
  const { enqueueSnackbar } = useSnackbar();
  const [metaData, setMetaDataInfo] = useState([]);
  const dispatch = useDispatch();
  const directionRef = useRef();
  const depthRef = useRef();
  const fieldOFViewRef = useRef();

  const originLatitudeRef = useRef();
  const originLongitudeRef = useRef();

  const geoLocationLatitudeRef = useRef();
  const geoLocationLongitudeRef = useRef();

  const coordinateXRef = useRef();
  const coordinateYRef = useRef();

  const nameRef = useRef();
  const tagsRef = useRef();
  const { adaptorType } = window;

  useEffect(() => {
    if (camera && adaptorType === 'streamer') {
      const getFileMetaData = () => {
        axiosInstance
          .get(`${window.location.protocol}//${camera.host}/api/v1/storage/file/metadata?streamId=${camera.sensorId}`)
          .then(async (response) => {
            if (response.data) {
              const parsedData = [];
              Object.keys(response.data).forEach((key, index) => {
                const temp = {
                  serial_number: index,
                  property: key,
                  value: response.data[key],
                };
                parsedData.push(temp);
              });
              setMetaDataInfo(parsedData);
              enqueueSnackbar(`Success - fetched ${camera.name} metadata info`, {
                variant: 'success',
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
              });
            }
          })
          .catch((e) => {
            console.log('Filed to get file metadata', e);
            enqueueSnackbar(`Error - ${e.response.data.error_message}`, {
              variant: 'error',
              anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
            });
          });
      };
      getFileMetaData();
    }
  }, []);

  const handleSubmit = () => {
    enqueueSnackbar(`Pending - update ${camera.name} info`, {
      variant: 'info',
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    });
    const jsonData = {
      name: nameRef.current.value, 
      position: {
        direction: directionRef.current.value,
        depth: depthRef.current.value,
        fieldOfView: fieldOFViewRef.current.value,
        origin: {
          latitude: originLatitudeRef.current.value,
          longitude: originLongitudeRef.current.value,
        },
        geoLocation: {
          latitude: geoLocationLatitudeRef.current.value,
          longitude: geoLocationLongitudeRef.current.value,
        },
        coordinates: {
          x: coordinateXRef.current.value,
          y: coordinateYRef.current.value,
        }
      }
    };
    if (adaptorType === 'streamer') {
      jsonData.tags = tagsRef.current.value;
    }
    if (window.liveStreamingCameras) {
      for(let i=0; i<window.liveStreamingCameras.length; i+=1) {
        if(window.liveStreamingCameras[i].id === camera.sensorId) {
          enqueueSnackbar(`Error - Please close stream before updating info`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
          return;
        }
      }
    }
    if (window.recordedStreamingCameras) {
      for(let i=0; i<window.recordedStreamingCameras.length; i+=1) {
        if(window.recordedStreamingCameras[i].id === camera.sensorId) {
          enqueueSnackbar(`Error - Please close stream before updating info`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
          return;
        }
      }
    }
    axiosInstance
      .post(`${window.location.protocol}//${camera.host}/api/v1/sensor/${camera.sensorId}/info`, jsonData)
      .then(async () => {
        enqueueSnackbar(`Success - ${camera.name} info updated`, {
          variant: 'success',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        const cameraList = await fetchDeviceList(camera.host);
        if (cameraList != null) {
          for (let i = 0; i < cameraList.length; i += 1) {
            cameraList[i].host = camera.host;
          }
          dispatch(appSliceActions.addDevice(cameraList));
        }
        const streamList = await fetchStreamList(camera.host);
        if (streamList != null) {
          for (let i = 0; i < streamList.length; i += 1) {
            streamList[i].host = camera.host;
          }
          dispatch(appSliceActions.addStream(streamList));
        }
      })
      .catch((error) => {
        enqueueSnackbar(`Error - Failed to set ${camera.name} info`, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
        });
        if (error.response.data == null)
          enqueueSnackbar(`Error - ${error.response.status}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        else
          enqueueSnackbar(`Error - ${error.response.data.error_message}`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
      });
  };

  return (
    <>
      <Stack spacing={3} sx={{ mb: 2 }}>
        {cameraInfo != null && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                General info
              </Typography>
            </Divider>
            <TextField name="name" label="name" defaultValue={cameraInfo.name} inputRef={nameRef} />
            {adaptorType === 'streamer' && (
              <TextField name="tags" label="tags" defaultValue={cameraInfo.tags} inputRef={tagsRef} />
            )}
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Position info
              </Typography>
            </Divider>
            <TextField
              name="direction"
              label="direction"
              defaultValue={cameraInfo.position.direction}
              inputRef={directionRef}
            />
            <TextField name="depth" label="depth" defaultValue={cameraInfo.position.depth} inputRef={depthRef} />
            <TextField
              name="field Of view"
              label="field of view"
              defaultValue={cameraInfo.position.fieldOfView}
              inputRef={fieldOFViewRef}
            />
            <Divider sx={{ my: 2 }} />
            <TextField
              name="origin latitude"
              label="origin latitude"
              defaultValue={cameraInfo.position.origin.latitude}
              inputRef={originLatitudeRef}
            />
            <TextField
              name="origin longitude"
              label="origin longitude"
              defaultValue={cameraInfo.position.origin.longitude}
              inputRef={originLongitudeRef}
            />
            <Divider sx={{ my: 2 }} />
            <TextField
              name="geo location latitude"
              label="geo location latitude"
              defaultValue={cameraInfo.position.geoLocation.latitude}
              inputRef={geoLocationLatitudeRef}
            />
            <TextField
              name="geo location longitude"
              label="geo location longitude"
              defaultValue={cameraInfo.position.geoLocation.longitude}
              inputRef={geoLocationLongitudeRef}
            />
            <Divider sx={{ my: 2 }} />
            <TextField
              name="coordinate x"
              label="coordinate x"
              defaultValue={cameraInfo.position.coordinates.x}
              inputRef={coordinateXRef}
            />
            <TextField
              name="coordinate y"
              label="coordinate y"
              defaultValue={cameraInfo.position.coordinates.y}
              inputRef={coordinateYRef}
            />
            {cameraInfo != null && (
              <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
                <LoadingButton size="large" type="submit" variant="contained" onClick={handleSubmit}>
                  Submit
                </LoadingButton>
              </Box>
            )}
            {adaptorType !== 'streamer' && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Below fields are not editable
                  </Typography>
                </Divider>
                <TextField name="hardware" label="hardware" defaultValue={cameraInfo.hardware} disabled />
                <TextField name="hardware ID" label="hardware ID" defaultValue={cameraInfo.hardwareId} disabled />
                <TextField name="IP" label="IP" defaultValue={cameraInfo.sensorIp} disabled />
                <TextField name="location" label="location" defaultValue={cameraInfo.location} disabled />
                <TextField name="manufacturer" label="manufacturer" defaultValue={cameraInfo.manufacturer} disabled />
                <TextField
                  name="serial number"
                  label="serial number"
                  defaultValue={cameraInfo.serialNumber}
                  disabled
                />
              </>
            )}
            {adaptorType === 'streamer' && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Metadata info
                  </Typography>
                </Divider>
                <Box>
                  <EnhancedTable headCells={headCells} rowData={metaData} rowCount={12} />
                </Box>
              </>
            )}
          </>
        )}
      </Stack>
    </>
  );
}

CameraInfo.propTypes = {
  cameraInfo: PropTypes.shape({
    firmwareVersion: PropTypes.string.isRequired,
    hardware: PropTypes.string.isRequired,
    hardwareId: PropTypes.string.isRequired,
    sensorId: PropTypes.string.isRequired,
    sensorIp: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    tags: PropTypes.string,
    manufacturer: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    position: PropTypes.shape({
      depth: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
      fieldOfView: PropTypes.string.isRequired,
      geoLocation: PropTypes.shape({
        latitude: PropTypes.string.isRequired,
        longitude: PropTypes.string.isRequired,
      }),
      coordinates: PropTypes.shape({
        x: PropTypes.string.isRequired,
        y: PropTypes.string.isRequired,
      }),
      origin: PropTypes.shape({
        latitude: PropTypes.string.isRequired,
        longitude: PropTypes.string.isRequired,
      }),
    }),
    serialNumber: PropTypes.string.isRequired,
  }).isRequired,
  camera: PropTypes.shape({
    sensorId: PropTypes.string.isRequired,
    host: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
